import { useApiQuery, useApiMutation, useApiDelete } from "lib/react-query";
import customFetch from "services/api";
import { HAS_PERMISSION } from "services/user";
import { AGENT_OPTIONS_INDEX } from "permissions";
import { isPrivateDomainSettling, PRIVATE_DOMAIN_POLL_MS } from "../constants";

// Settings
export const useSettings = (enabled = true) =>
  useApiQuery(enabled ? "dashboard/agent/settings" : null);

/**
 * The same `dashboard/agent/settings` payload `useSettings` reads, fetched at
 * app boot so the tenant's favicon is known the first time the ERP is opened
 * rather than only after a Settings visit — see `useFaviconBootstrap`. It is
 * NOT `useSettings` reused as-is:
 * - `enabled` is gated on `AGENT_OPTIONS_INDEX` up front, so a user without it
 *   never fires a request that can only come back 403.
 * - `returnFullErrorResponse` skips the global error handler, so a request
 *   that fails anyway (a stale permission cache, a network hiccup) never
 *   surfaces a "no permission" toast on an app-open nobody asked to see.
 * - A distinct `queryKey` keeps its cache entry separate from `useSettings`'s,
 *   since two observers sharing one key would otherwise share one `queryFn`.
 */
export const useAgentFaviconBootstrap = () =>
  useApiQuery(
    "dashboard/agent/settings",
    null,
    false,
    null,
    { returnFullErrorResponse: true },
    {
      enabled: HAS_PERMISSION(AGENT_OPTIONS_INDEX),
      queryKey: ["dashboard/agent/settings", "favicon-bootstrap"],
      staleTime: Infinity,
      retry: false,
    }
  );

/**
 * The tenant's public settings (`corporate/settings`) — the endpoint
 * the corporate website reads, unauthenticated, so the login screen can ask it
 * before anyone signs in and every user gets the same answer regardless of
 * `AGENT_OPTIONS_INDEX`. Read through `useTenantLogo` for the sidebar and
 * login logo; `selectTenantLogoUrl` picks the logo out of it.
 * - `returnFullErrorResponse` skips the global error handler: a failed read
 *   falls back to the Amer logo and must never toast on the login screen.
 * - `suppressGlobalError` keeps a network failure off the global error page.
 */
export const usePublicTenantSettings = () =>
  useApiQuery(
    `corporate/settings`,
    null,
    false,
    null,
    { returnFullErrorResponse: true, suppressGlobalError: true },
    { retry: false, refetchOnWindowFocus: false }
  );

/**
 * `company_info`'s logo URL from the public settings, `null` when the tenant
 * has none, or `undefined` when the payload says nothing (a failed read).
 * The payload is a list of option blocks (`[{ company_info }, …]`), and there
 * `logo` is already a full URL; `logo_url` is preferred should it appear.
 */
export const selectTenantLogoUrl = (data) => {
  const blocks = Array.isArray(data) ? data : [data];
  const companyInfo = blocks.find((block) => block?.company_info)?.company_info;
  if (!companyInfo) return undefined;
  return companyInfo.logo_url || companyInfo.logo || null;
};

// Zatca
export const useZatcaStatus = () => useApiQuery("dashboard/agent/zatca/status");

// Integrations status for Settings Hub
export const useIntegrationsQuery = () =>
  useApiQuery("dashboard/agent/integrations");

export const revokeZatca = (body) =>
  customFetch("dashboard/agent/zatca/revoke", { method: "POST", body });

export const integrateZatca = (body) =>
  customFetch("dashboard/agent/zatca/integrate", { method: "POST", body });

// Ejar (EJR) credentials
export const useEjarCredentials = () =>
  useApiQuery("dashboard/agent/settings/ejar-credentials");

/**
 * Whether this office has stored its own Ejar credentials — `data` is a bare
 * boolean, not an object.
 *
 * The same `is_ejar_enabled()` the backend gates every Ejar action on a
 * contract with, so a screen that offers an Ejar feature should ask this rather
 * than reading the credentials. Unlike `useEjarCredentials`, it carries no
 * permission: the answer is a yes/no about the office's setup, not the
 * credentials themselves.
 */
export const useEjarStatus = () =>
  useApiQuery("dashboard/agent/settings/ejar-status");

// ---------------------------------------------------------------------------
// CRM settings
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Letters settings
// ---------------------------------------------------------------------------

export const LETTER_SETTINGS_URL = "dashboard/agent/settings/letters";

/**
 * The letters workflow toggles (require review / require approval). The
 * endpoint merges the stored value over the defaults, so a tenant that never
 * saved still answers both keys.
 */
export const useLetterSettings = () => useApiQuery(LETTER_SETTINGS_URL);

// ---------------------------------------------------------------------------
// HR settings
// ---------------------------------------------------------------------------

export const HR_SETTINGS_URL = "dashboard/agent/settings/hr";

/**
 * The tenant's HR configuration — pay cycle, which country id means Saudi
 * Arabia, and the government-ID expiry warning window. The endpoint merges the
 * stored value over the defaults, so a tenant that never saved answers every key.
 */
export const useHrSettings = () => useApiQuery(HR_SETTINGS_URL);

// ---------------------------------------------------------------------------
// Maintenance settings
// ---------------------------------------------------------------------------

export const MAINTENANCE_SETTINGS_URL = "dashboard/agent/settings/maintenance";

/** The maintenance timings, all in minutes; merged over the defaults by the endpoint. */
export const useMaintenanceSettings = () =>
  useApiQuery(MAINTENANCE_SETTINGS_URL);
// Ads cross listing
// ---------------------------------------------------------------------------

export const ADS_CROSS_LISTING_URL =
  "dashboard/agent/settings/ads-cross-listing";

/**
 * Per-portal ad cross-listing settings, keyed by portal (`aqar`).
 *
 * The client secret is never returned — only `has_client_secret` — so the form
 * shows a masked placeholder instead of an empty box on an office that already
 * saved one. `is_ready` is the portal being enabled AND holding both
 * credentials, which is the state the publish toggle checks.
 */
export const useAdsCrossListing = () => useApiQuery(ADS_CROSS_LISTING_URL);

// ---------------------------------------------------------------------------
// Sadq — e-signature
// ---------------------------------------------------------------------------

export const SADQ_SETTINGS_URL = "dashboard/agent/settings/sadq";
export const SADQ_WEBHOOK_URL = `${SADQ_SETTINGS_URL}/webhook`;

/**
 * This office's Sadq account, as the settings panel reads it.
 *
 * Neither secret comes back — only `has_account_secret` / `has_password` — so
 * the form shows a masked placeholder instead of an empty box on an office that
 * already saved one, and an untouched field keeps what is stored. `is_ready` is
 * Sadq being enabled AND all four credentials present, which is the state the
 * e-signature module checks before it offers to send anything; `webhook_url` is
 * the address we would register on this tenant's own subdomain, so the office
 * can see it before pressing Register.
 */
export const useSadqSettings = () => useApiQuery(SADQ_SETTINGS_URL);

// ---------------------------------------------------------------------------
// SMS provider (Msegat) — AMER-5083
// ---------------------------------------------------------------------------

export const SMS_PROVIDER_URL = "dashboard/agent/settings/sms-provider";
export const SMS_PROVIDER_BALANCE_URL = `${SMS_PROVIDER_URL}/balance`;
export const SMS_PROVIDER_REQUEST_URL = `${SMS_PROVIDER_URL}/service-request`;
export const SMS_PROVIDER_LETTER_URL = `${SMS_PROVIDER_URL}/authorization-letter`;
export const SMS_PROVIDER_TEST_SEND_URL = `${SMS_PROVIDER_URL}/test-send`;

/**
 * Send one test SMS through the office's own Msegat account. Resolves with the
 * envelope: `data.ok` says whether Msegat accepted it, with its `code` and
 * `message` — a Msegat refusal is a result, not an error.
 */
export const sendSmsProviderTest = (body) =>
  customFetch(SMS_PROVIDER_TEST_SEND_URL, { method: "POST", body });

/**
 * The office's own SMS provider account: `status` (not_connected / requested /
 * connected) with a bilingual `status_label`, the sender name and username,
 * `has_api_key` (never the key), the 350 SAR `fee`, the last service request,
 * and `request_prefill` — the request form's contact fields from the signed-in
 * user.
 */
export const useSmsProvider = () => useApiQuery(SMS_PROVIDER_URL);

/**
 * The remaining balance, read live from Msegat on every fetch. Pass `enabled`
 * false while no account is connected — the backend answers 409 then.
 *
 * `data` is `{ ok, balance, sender_name, error_code, checked_at }`
 * (`SmsProviderService::balance()`): a Msegat refusal still answers 200, with
 * `ok` false and Msegat's `error_code`, so `ok` is the field that tells a read
 * balance from a refused one.
 */
export const useSmsProviderBalance = (enabled) =>
  useApiQuery(enabled ? SMS_PROVIDER_BALANCE_URL : null);

// ---------------------------------------------------------------------------
// Private domain
// ---------------------------------------------------------------------------

export const PRIVATE_DOMAIN_URL = "dashboard/agent/settings/private-domain";
export const PRIVATE_DOMAIN_CHECK_URL = `${PRIVATE_DOMAIN_URL}/check`;
export const PRIVATE_DOMAIN_SEARCH_URL = `${PRIVATE_DOMAIN_URL}/search`;
export const PRIVATE_DOMAIN_REGISTER_URL = `${PRIVATE_DOMAIN_URL}/register`;
export const PRIVATE_DOMAIN_CONNECT_URL = `${PRIVATE_DOMAIN_URL}/connect`;
export const PRIVATE_DOMAIN_TRANSFER_URL = `${PRIVATE_DOMAIN_URL}/transfer`;
export const PRIVATE_DOMAIN_SETTINGS_URL = `${PRIVATE_DOMAIN_URL}/settings`;
export const PRIVATE_DOMAIN_RENEWAL_PAY_URL = `${PRIVATE_DOMAIN_URL}/renewal/pay`;
export const PRIVATE_DOMAIN_RECORDS_URL = `${PRIVATE_DOMAIN_URL}/records`;
export const privateDomainRecordUrl = (id) =>
  `${PRIVATE_DOMAIN_RECORDS_URL}/${id}`;

/**
 * The office's private domain: `status` with a bilingual `status_label`, the
 * domain and how it came here, its nameservers, the registration and any
 * registrar transfer, the registrant prefill and the history.
 *
 * Read again every 30 seconds while a setup or a transfer is still moving — the
 * backend advances both on its own, and the office is usually watching the page.
 */
export const usePrivateDomain = () =>
  useApiQuery(
    PRIVATE_DOMAIN_URL,
    null,
    false,
    null,
    {},
    {
      // `query.state.data` is the raw envelope; the overview is its `data`.
      refetchInterval: (query) =>
        isPrivateDomainSettling(query.state.data?.data)
          ? PRIVATE_DOMAIN_POLL_MS
          : false,
    }
  );

/**
 * The zone's DNS records (`records`) and, while the nameservers are being
 * switched, the records found on the domain's current DNS (`discovered`). Both
 * are empty until a zone exists.
 */
export const usePrivateDomainRecords = (enabled) =>
  useApiQuery(enabled ? PRIVATE_DOMAIN_RECORDS_URL : null);

/** Domains to register for a name, each with whether the activation fee covers it. */
export const usePrivateDomainSearch = (query) =>
  useApiQuery(
    query ? `${PRIVATE_DOMAIN_SEARCH_URL}?q=${encodeURIComponent(query)}` : null
  );

export const CRM_SETTINGS_URL = "dashboard/agent/settings/crm";
export const WEB_TO_LEAD_KEY_URL = `${CRM_SETTINGS_URL}/web-to-lead-key`;

/**
 * How this tenant's CRM behaves, plus the option sets the screen builds choices from.
 *
 * One request rather than three: the assignable users and lead sources come back with the
 * settings, so a routing rule can never be built against a source that no longer exists.
 * Defaults are filled in server-side, so a tenant who has never saved gets a complete
 * document and the form has nothing to guess.
 */
export const useCrmSettings = () => useApiQuery(CRM_SETTINGS_URL);

// Sections (Landing Page)
export const useSections = () => useApiQuery("dashboard/sections");

export const useProjectsSearch = (name) =>
  useApiQuery(
    `dashboard/projects/search?search=${encodeURIComponent(name ?? "")}&deleted=false`
  );

// Generic settings hooks (kept for backward compatibility)
export const useSettingsList = (url, page, hasSearchParams, otherParam) => {
  return useApiQuery(url, page, hasSearchParams, otherParam);
};

export const useSettingsDetail = (url) => {
  return useApiQuery(url);
};

export const useSettingsMutation = (callbacks = {}) => {
  return useApiMutation(callbacks);
};

export const useSettingsDelete = (baseUrl, setData) => {
  return useApiDelete(baseUrl, setData);
};
